import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";

export interface CustomImpactReferenceDatasetBase {
  name: string;
  geography: string;
  unit: string;
  elementary_eco_invent_dataset_id: number | null;
  note: string;
}

export interface CustomImpactReferenceDataset extends CustomImpactReferenceDatasetBase {
  id: number;
}

export interface CustomImpactReferenceExchange {
  id: number;
  dataset: CustomImpactReferenceDataset;
  amount: number;
  not_absolute: boolean;
}
export interface CustomImpactReferenceElementaryExchange {
  name: string;
  unit: string;
  amount: number;
}

export interface GetCustomImpactReference {
  dataset: CustomImpactReferenceDataset;
  exchanges: CustomImpactReferenceExchange[];
  elementary_exchanges: CustomImpactReferenceElementaryExchange[];
}

export function useGetCustomImpactReference({
  id,
  fromEcoInventDataset,
}: {
  id?: number;
  fromEcoInventDataset?: boolean;
}) {
  return useQuery({
    queryKey: ["custom_impact_reference", id],
    keepPreviousData: true,
    enabled: !!id,
    queryFn: () =>
      axios
        .get<GetCustomImpactReference>(
          "/impact_references/custom/" +
            id +
            "?from_eco_invent_dataset=" +
            Boolean(fromEcoInventDataset)
        )
        .then((result) => result.data),
  });
}

export interface PostExchange {
  id: number;
  amount: number;
}

export interface PostCustomImpactReference {
  dataset: CustomImpactReferenceDatasetBase;
  exchanges: PostExchange[];
}

export function useInsertCustomImpactReference() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ post }: { post: PostCustomImpactReference }) =>
      axios.post("/impact_references/custom", post),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["datasets"]);
    },
  });
}

export function useUpdateCustomImpactReference() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ id, post }: { id: number; post: PostCustomImpactReference }) =>
      axios.post("/impact_references/custom/" + id, post),
    onSuccess: (response) => {
      smartQueryClient.invalidateQueries(["custom_impact_reference", response.data.id]);
      smartQueryClient.invalidateQueries(["datasets"]);
    },
  });
}

export function useDeleteCustomImpactReference() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id?: number }) => axios.delete("/impact_references/custom/" + id),
    onSuccess: (response) => {
      smartQueryClient.invalidateQueries(["datasets"]);
    },
  });
}
